import React, { useContext } from 'react'
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@chakra-ui/accordion'
import { Box, Flex, Link, VStack, Text as ChakraText } from '@chakra-ui/layout'
import { Switch } from '@chakra-ui/switch'
import type { FormikProps } from 'formik'
import { BsShieldLock } from 'react-icons/bs'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../../ui-kit'
import { SettingsToggleBox } from '..'
import type { SettingsResponse } from '../../../interfaces'
import PermissionGuard from '../../PermissionGuard'
import { PermissionsEnum } from '../../../../utils'

const VideoSettingsSecurity = ({
  values,
  handleChange,
}: FormikProps<SettingsResponse>): React.ReactElement => {
  const { t } = useTranslation()


  return (
    <PermissionGuard
      permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
    >
      {
        (hasPermission: boolean) => (
          <AccordionItem>
            <AccordionButton px={12} py={8}>
              <Flex
                flex="1"
                textAlign="left"
                lineHeight="1.5em"
                fontWeight="700"
                style={{ gap: '1em' }}
              >
                <BsShieldLock fontSize="1.5em" />
                {t('video_security', 'Security')}
              </Flex>
              <Box
                background={colors.appPaleGreyThree}
                borderRadius="50%"
                padding="0.25em"
              >
                <AccordionIcon color={colors.appSlate} display="block" />
              </Box>
            </AccordionButton>

            <AccordionPanel px={12} py={8} position="relative">
              <VStack spacing="0.75em">
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t('video_restrict_unsigned')}
                    </ChakraText>
                    <Switch
                      name="videoSettings.security.onlySigned"
                      isDisabled={!hasPermission}
                      isChecked={values.videoSettings.security.onlySigned}
                      onChange={handleChange}
                    />
                  </Flex>
                  <ChakraText>
                    {t('video_restrict_unsigned_description')}{' '}
                    <Link
                      isExternal
                      href="https://imagekit.io/docs/media-delivery-basic-security#restrict-unsigned-urls"
                    >
                      {t('read_more')}
                    </Link>
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appGreenishTeal">
                      OFF
                    </ChakraText>
                    {t('video_restrict_unsigned_recommendation')}
                  </ChakraText>
                </SettingsToggleBox>
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t('video_restrict_unnamed_transform')}
                    </ChakraText>
                    <Switch
                      name="videoSettings.security.onlyNamedTransformations"
                      isDisabled={!hasPermission}
                      isChecked={
                        values.videoSettings.security.onlyNamedTransformations
                      }
                      onChange={handleChange}
                    />
                  </Flex>
                  <ChakraText>
                    {t('video_restrict_unnamed_transform_description')}{' '}
                    <Link
                      isExternal
                      href="https://docs.imagekit.io/features/security#restricting-unnamed-image-transformations"
                    >
                      {t('read_more')}
                    </Link>
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appGreenishTeal">
                      OFF
                    </ChakraText>
                    {t('video_restrict_unnamed_transform_recommendation')}
                  </ChakraText>
                </SettingsToggleBox>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        )
      }
    </PermissionGuard>
  )
}

export default VideoSettingsSecurity
